import * as ExcelJS from 'exceljs';

export const HEADER_KEY_ALIASES: Record<string, string> = {
  // Message labels
  read: 'trigger',
  when: 'comparison',
  is: 'value',
  refresh_interval_for_polling: 'refresh_interval',

  // Asset/component labels
  root_tag_for_scanning_can_be_empty: 'scan_root',
  root_tag_for_scanning: 'scan_root',
  tls_algorithm: 'tls_algo',
  opc_username: 'username',
  opc_password: 'password',
  private_key: 'privateKey',

  // Secret labels
  secrets_user_credentials: 'secret_user_credentials',
  secrets__user_credentials: 'secret_user_credentials',
  secrets_auth_credentials: 'secret_auth_credentials',
  secrets__auth_credentials: 'secret_auth_credentials',
};

export const canonicalizeHeaderKey = (key: string): string => HEADER_KEY_ALIASES[key] ?? key;

export const statusToEnabled = (statusRaw: any): boolean => {
  const normalized = String(statusRaw ?? '').trim().toLowerCase();
  if (!normalized) return true;
  if (['inactive', 'disable', 'disabled', 'false', '0', 'no', 'off'].includes(normalized)) return false;
  if (['active', 'enable', 'enabled', 'true', '1', 'yes', 'on'].includes(normalized)) return true;
  return true;
};

export const toStr = (raw: ExcelJS.CellValue | undefined): string => {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'object') {
    const asObj = raw as any;
    if ('result' in asObj) return String(asObj.result ?? '').trim();
    if ('richText' in asObj) {
      return (asObj.richText as any[]).map((rt: any) => rt.text ?? '').join('').trim();
    }
    return '';
  }
  return String(raw).trim();
};

export const normalizeHeader = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/^_+|_+$/g, '');

export const detectHeader = (
  worksheet: ExcelJS.Worksheet,
  requiredKeys: string[],
  maxScan = 20
): { rowNumber: number; keys: string[] } | null => {
  const limit = Math.min(maxScan, worksheet.rowCount);

  for (let r = 1; r <= limit; r++) {
    const row = worksheet.getRow(r);
    const rawValues: string[] = [];

    row.eachCell({ includeEmpty: false }, (cell) => {
      rawValues.push(normalizeHeader(toStr(cell.value)));
    });

    const hasRequired = requiredKeys.every((key) => rawValues.includes(key));
    if (!hasRequired) continue;

    const keys: string[] = [];
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      keys[colNumber - 1] = normalizeHeader(toStr(cell.value));
    });
    return { rowNumber: r, keys };
  }

  return null;
};

export const readDataRows = (
  worksheet: ExcelJS.Worksheet,
  headerRowNumber: number,
  keys: string[],
  dataStartOffset = 2
): Array<Record<string, string>> => {
  const dataStartRow = headerRowNumber + dataStartOffset;
  const result: Array<Record<string, string>> = [];

  for (let r = dataStartRow; r <= worksheet.rowCount; r++) {
    const row = worksheet.getRow(r);
    if (row.actualCellCount === 0) continue;

    const obj: Record<string, string> = {};
    let hasValue = false;

    keys.forEach((key, colIdx) => {
      if (!key) return;
      const canonicalKey = canonicalizeHeaderKey(key);
      const val = toStr(row.getCell(colIdx + 1).value);
      if (!(canonicalKey in obj) || (obj[canonicalKey] === '' && val !== '')) {
        obj[canonicalKey] = val;
      }
      if (val) hasValue = true;
    });

    if (hasValue) result.push(obj);
  }

  return result;
};
